/**
 * PostViewTracker - Service for tracking post views
 *
 * Tracks when posts are visible in viewport for 2+ seconds
 * Batches views and sends to API periodically
 * Manages duplicate prevention and optimistic UI updates
 */
class PostViewTracker {
  constructor() {
    // Track active post observers
    this.trackedPosts = new Map(); // postId -> { element, observer, timer, startTime }

    // Batch queue for API calls
    this.batchQueue = []; // [{ post_id, timestamp }]

    // Duplicate prevention - posts already sent to API
    this.sentViews = new Set(); // Set of postIds

    // Posts queued (pending in batchQueue) - prevents duplicate tracking before flush
    this.queuedViews = new Set(); // Set of postIds in batchQueue

    // Optimistic view counts (local state)
    this.viewCounts = new Map(); // postId -> count

    // Flush interval
    this.flushInterval = null;
    this.lastFlushTime = null;

    // Configuration
    this.config = {
      MIN_VISIBILITY_TIME: 2000, // 2 seconds in ms
      BATCH_SIZE_THRESHOLD: 50, // Flush when queue reaches this size
      FLUSH_INTERVAL: 15000, // 15 seconds
      MAX_BATCH_SIZE: 100, // API limit
    };

    // Callbacks
    this.onViewCountUpdate = null; // Callback for view count updates

    // Start periodic flush
    this.startPeriodicFlush();
  }

  /**
   * Set callback for view count updates
   * @param {Function} callback - (postId, count) => void
   */
  setViewCountUpdateCallback(callback) {
    this.onViewCountUpdate = callback;
  }

  /**
   * Start tracking a post
   * @param {number} postId - Post ID
   * @param {HTMLElement} element - Post DOM element
   * @param {number} initialViewCount - Initial view count from API
   * @param {boolean} isViewedByCurrentUser - Whether post is already viewed by current user
   * @param {boolean} forceRestart - Force restart tracking even if already tracking
   */
  startTracking(
    postId,
    element,
    initialViewCount = 0,
    isViewedByCurrentUser = false,
    forceRestart = false,
  ) {
    // Don't track if already viewed by current user
    if (isViewedByCurrentUser) {
      // But still update view count and stop any existing tracking
      if (initialViewCount !== undefined && initialViewCount !== null) {
        this.viewCounts.set(postId, initialViewCount);
        if (this.onViewCountUpdate) {
          this.onViewCountUpdate(postId, initialViewCount);
        }
      }
      // Stop tracking if it was being tracked
      if (this.trackedPosts.has(postId)) {
        this.stopTracking(postId);
      }
      return;
    }

    // Don't track if already sent (lifetime duplicate rule) - unless forcing restart
    if (this.sentViews.has(postId) && !forceRestart) {
      return;
    }

    // If already tracking and element changed, stop old tracking first
    if (this.trackedPosts.has(postId)) {
      const existingData = this.trackedPosts.get(postId);
      // If element changed or force restart, stop old tracking
      if (forceRestart || existingData.element !== element) {
        this.stopTracking(postId);
      } else {
        // Same element, already tracking - just update view count if needed
        if (initialViewCount !== undefined && initialViewCount !== null) {
          this.viewCounts.set(postId, initialViewCount);
          if (this.onViewCountUpdate) {
            this.onViewCountUpdate(postId, initialViewCount);
          }
        }
        return;
      }
    }

    // Set initial view count
    if (initialViewCount !== undefined && initialViewCount !== null) {
      this.viewCounts.set(postId, initialViewCount);
    }

    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const trackingData = this.trackedPosts.get(postId);
          if (!trackingData) return;

          if (entry.isIntersecting) {
            // Post entered viewport
            if (!trackingData.timer) {
              // Start 2-second timer
              trackingData.startTime = Date.now();
              trackingData.timer = setTimeout(() => {
                this.handleViewThresholdMet(postId);
              }, this.config.MIN_VISIBILITY_TIME);
            }
          } else {
            // Post left viewport
            if (trackingData.timer) {
              // Cancel timer if post left before 2 seconds
              clearTimeout(trackingData.timer);
              trackingData.timer = null;
              trackingData.startTime = null;
            }
          }
        });
      },
      {
        threshold: 0.5, // At least 50% visible
        rootMargin: "0px",
      },
    );

    // Start observing
    observer.observe(element);

    // Store tracking data
    this.trackedPosts.set(postId, {
      element,
      observer,
      timer: null,
      startTime: null,
    });
  }

  /**
   * Stop tracking a post
   * @param {number} postId - Post ID
   */
  stopTracking(postId) {
    const trackingData = this.trackedPosts.get(postId);
    if (!trackingData) return;

    // Clean up
    if (trackingData.timer) {
      clearTimeout(trackingData.timer);
    }
    if (trackingData.observer) {
      trackingData.observer.disconnect();
    }

    // Remove from tracking
    this.trackedPosts.delete(postId);
  }

  /**
   * Handle when post has been visible for 2+ seconds
   * @param {number} postId - Post ID
   */
  handleViewThresholdMet(postId) {
    // Don't track if already sent to API
    if (this.sentViews.has(postId)) {
      return;
    }

    // Don't track if already queued (prevents duplicate before flush)
    if (this.queuedViews.has(postId)) {
      return;
    }

    // Add to batch queue
    const viewData = {
      post_id: postId,
      timestamp: Date.now(),
    };

    this.batchQueue.push(viewData);
    this.queuedViews.add(postId); // Mark as queued

    // Update optimistic view count
    const currentCount = this.viewCounts.get(postId) || 0;
    const newCount = currentCount + 1;
    this.viewCounts.set(postId, newCount);

    // Notify callback
    if (this.onViewCountUpdate) {
      this.onViewCountUpdate(postId, newCount);
    }

    // Mark timer as completed
    const trackingData = this.trackedPosts.get(postId);
    if (trackingData) {
      trackingData.timer = null;
      trackingData.startTime = null;
    }

    // Check if we should flush
    if (this.batchQueue.length >= this.config.BATCH_SIZE_THRESHOLD) {
      this.flushBatch();
    }
  }

  /**
   * Get current view count for a post (optimistic)
   * @param {number} postId - Post ID
   * @returns {number} View count
   */
  getViewCount(postId) {
    return this.viewCounts.get(postId) || 0;
  }

  /**
   * Update view count from API response
   * @param {number} postId - Post ID
   * @param {number} count - View count from API
   * @param {boolean} forceUpdate - Force update even if optimistic count is higher
   */
  updateViewCount(postId, count, forceUpdate = false) {
    if (count !== undefined && count !== null) {
      const currentCount = this.viewCounts.get(postId) || 0;

      // If there's a pending view in the queue, preserve the optimistic update
      // unless forceUpdate is true or the API count is higher
      if (!forceUpdate && this.queuedViews.has(postId)) {
        // Don't overwrite optimistic update if API count is lower
        // This can happen if posts are fetched before the batch is flushed
        if (count < currentCount) {
          return; // Keep optimistic count
        }
      }

      // Update to API count (authoritative)
      this.viewCounts.set(postId, count);
      if (this.onViewCountUpdate) {
        this.onViewCountUpdate(postId, count);
      }
    }
  }

  /**
   * Check if post has already been tracked (sent to API or queued)
   * @param {number} postId - Post ID
   * @returns {boolean}
   */
  isAlreadyTracked(postId) {
    return this.sentViews.has(postId) || this.queuedViews.has(postId);
  }

  /**
   * Get batch queue (for API call)
   * @returns {Array} Array of view data
   */
  getBatchQueue() {
    return [...this.batchQueue];
  }

  /**
   * Flush batch queue to API
   * @param {Function} apiCall - Function to call API
   * @returns {Promise} API response
   */
  async flushBatch(apiCall) {
    if (this.batchQueue.length === 0) {
      return;
    }

    if (!apiCall) {
      console.warn("PostViewTracker: No API call function provided");
      return;
    }

    // Get current batch (limit to MAX_BATCH_SIZE)
    const batchToSend = this.batchQueue.slice(0, this.config.MAX_BATCH_SIZE);
    const batchPostIds = new Set(batchToSend.map((v) => v.post_id));

    try {
      // Call API
      const response = await apiCall({ views: batchToSend });

      // Handle response
      if (response?.data) {
        const { recorded, skipped, view_counts } = response.data;

        // Update view counts from API response if provided
        if (view_counts && typeof view_counts === "object") {
          Object.entries(view_counts).forEach(([postIdStr, count]) => {
            const postId = parseInt(postIdStr, 10);
            if (!isNaN(postId) && count !== undefined && count !== null) {
              // Force update from server (authoritative after batch flush)
              this.updateViewCount(postId, count, true);
            }
          });
        }

        // Mark all processed posts as sent (both recorded and skipped)
        // Skipped posts are already viewed or invalid, so we don't want to track them again
        batchPostIds.forEach((postId) => {
          this.sentViews.add(postId);
          this.queuedViews.delete(postId); // Remove from queued (now processed)
        });

        // Remove processed items from queue
        this.batchQueue = this.batchQueue.filter(
          (view) => !batchPostIds.has(view.post_id),
        );

        this.lastFlushTime = Date.now();
      }
    } catch (error) {
      console.error("PostViewTracker: Failed to flush batch", error);
      // Keep items in queue for retry
      // Don't mark as sent on error
      // Keep in queuedViews so they can be retried
    }
  }

  /**
   * Start periodic flush interval
   */
  startPeriodicFlush() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }

    this.flushInterval = setInterval(() => {
      if (this.batchQueue.length > 0) {
        // Will be called with API function from hook
        // This is just the interval setup
      }
    }, this.config.FLUSH_INTERVAL);

    this.lastFlushTime = Date.now();
  }

  /**
   * Stop periodic flush
   */
  stopPeriodicFlush() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }

  /**
   * Clean up all tracking
   */
  cleanup() {
    // Stop all tracking
    this.trackedPosts.forEach((data, postId) => {
      this.stopTracking(postId);
    });

    // Clear intervals
    this.stopPeriodicFlush();

    // Clear queues (but keep sentViews and viewCounts for persistence)
    // this.batchQueue = [];
    // this.sentViews.clear();
    // this.viewCounts.clear();
  }

  /**
   * Flush remaining views (for cleanup)
   * @param {Function} apiCall - Function to call API
   */
  async flushRemaining(apiCall) {
    if (this.batchQueue.length > 0 && apiCall) {
      await this.flushBatch(apiCall);
    }
  }
}

// Singleton instance
let trackerInstance = null;

/**
 * Get singleton instance of PostViewTracker
 * @returns {PostViewTracker}
 */
export const getPostViewTracker = () => {
  if (!trackerInstance) {
    trackerInstance = new PostViewTracker();
  }
  return trackerInstance;
};

export default getPostViewTracker;
